import { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  LayoutDashboard, Dumbbell, BookOpen, MessageSquare, NotebookPen, Shield,
  Users, ClipboardList, Clock, Menu, LogOut, ShieldCheck, CheckSquare, UserPlus, Settings,
} from "lucide-react";

const NAV = {
  paziente: [
    { to: "/patient", label: "Oggi", icon: LayoutDashboard, end: true },
    { to: "/patient/diary", label: "Diario", icon: NotebookPen },
    { to: "/patient/brace", label: "Busto", icon: Shield },
    { to: "/patient/education", label: "Articoli", icon: BookOpen },
    { to: "/patient/chat", label: "Chat", icon: MessageSquare },
    { to: "/profile", label: "Profilo", icon: Settings },
  ],
  fisioterapista: [
    { to: "/physio", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/physio/patients", label: "Pazienti", icon: Users },
    { to: "/physio/chat", label: "Chat", icon: MessageSquare },
    { to: "/physio/exercises", label: "Esercizi", icon: Dumbbell },
    { to: "/physio/articles", label: "Articoli", icon: BookOpen },
    { to: "/physio/assistance", label: "Minuti", icon: Clock },
    { to: "/profile", label: "Profilo", icon: Settings },
  ],
  admin: [
    { to: "/admin", label: "Statistiche", icon: LayoutDashboard, end: true },
    { to: "/admin/users", label: "Utenti", icon: Users },
    { to: "/admin/exercises", label: "Esercizi", icon: Dumbbell },
    { to: "/admin/articles", label: "Articoli", icon: BookOpen },
    { to: "/admin/assistance", label: "Minuti", icon: Clock },
    { to: "/admin/content", label: "Approvazioni", icon: CheckSquare },
    { to: "/profile", label: "Profilo", icon: Settings },
  ],
};

const roleLabel = { paziente: "Paziente", fisioterapista: "Fisioterapista", admin: "Amministratore" };

const BottomNav = ({ items, unread }) => (
  <nav className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-around border-t border-border/60 bg-white/95 px-1 py-1.5 backdrop-blur lg:hidden" data-testid="bottom-nav">
    {items.map((it) => {
      const showBadge = it.to.endsWith("/chat") && unread > 0;
      return (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          data-testid={`bottomnav-${it.label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex flex-1 flex-col items-center gap-0.5 rounded-lg py-1 text-[10px] font-medium transition-colors ${
              isActive ? "text-primary" : "text-muted-foreground"
            }`
          }
        >
          <span className="relative">
            <it.icon className="h-5 w-5" />
            {showBadge && (
              <span className="absolute -right-2 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white" data-testid="chat-unread-badge">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </span>
          {it.label}
        </NavLink>
      );
    })}
  </nav>
);

const NavItems = ({ items, onClick, unread }) => (
  <nav className="flex flex-col gap-1">
    {items.map((it) => {
      const showBadge = it.to.endsWith("/chat") && unread > 0;
      return (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={onClick}
          data-testid={`nav-${it.label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-all ${
              isActive
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-foreground/70 hover:bg-primary/10 hover:text-foreground"
            }`
          }
        >
          <it.icon className="h-[18px] w-[18px]" />
          <span className="flex-1">{it.label}</span>
          {showBadge && (
            <span className="grid h-5 min-w-5 place-items-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white" data-testid="chat-unread-badge-sidebar">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </NavLink>
      );
    })}
  </nav>
);

const Brand = () => (
  <div className="flex items-center gap-2.5 px-2">
    <img src="/logo-s2m.png" alt="Science2Movement" className="h-10 w-10 rounded-xl object-cover" />
    <div className="leading-tight">
      <p className="font-semibold text-foreground">Science2Movement</p>
      <p className="text-[11px] text-muted-foreground">Piattaforma clinica</p>
    </div>
  </div>
);

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const role = user?.role || "paziente";
  const items = NAV[role] || NAV.paziente;
  const hasChat = items.some((it) => it.to.endsWith("/chat"));
  const initials = (user?.full_name || user?.username || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const fetchUnread = useCallback(async () => {
    if (!hasChat) return;
    try {
      const d = await api.unreadCount();
      setUnread(typeof d === "number" ? d : (d?.count ?? d?.unread ?? 0));
    } catch { /* ignore */ }
  }, [hasChat]);

  useEffect(() => { fetchUnread(); }, [fetchUnread, location.pathname]);
  useEffect(() => {
    if (!hasChat) return;
    const t = setInterval(fetchUnread, 20000);
    return () => clearInterval(t);
  }, [hasChat, fetchUnread]);

  const Sidebar = ({ onNav }) => (
    <div className="flex h-full flex-col gap-6 p-4">
      <Brand />
      <NavItems items={items} onClick={onNav} unread={unread} />
      <div className="mt-auto rounded-2xl bg-muted/60 p-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 bg-primary/15">
            <AvatarFallback className="bg-transparent text-sm font-semibold text-primary">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{user?.full_name || user?.username}</p>
            <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <ShieldCheck className="h-3 w-3" /> {roleLabel[role]}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="mt-3 w-full" onClick={logout} data-testid="logout-button">
          <LogOut className="mr-2 h-4 w-4" /> Esci
        </Button>
      </div>
    </div>
  );

  return (
    <div className="s2m-gradient min-h-screen">
      <div className="mx-auto flex max-w-[1400px]">
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-border/60 bg-white/60 backdrop-blur-md lg:block">
          <Sidebar />
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="glass sticky top-0 z-30 flex items-center justify-between border-b border-border/60 px-4 py-3 lg:hidden">
            <Brand />
            <Avatar className="h-9 w-9 bg-primary/15">
              <AvatarFallback className="bg-transparent text-xs font-semibold text-primary">{initials}</AvatarFallback>
            </Avatar>
          </header>

          <main className="flex-1 px-4 py-6 pb-24 sm:px-8 sm:py-10 lg:pb-10">
            <div className="animate-fade-up">{children}</div>
          </main>
        </div>
      </div>
      <BottomNav items={items} unread={unread} />
    </div>
  );
};
